import {
  conditionalRequest,
  decodePage,
  isRecord,
  json,
  nullableString,
  request,
  requestNoContent,
  requireArray,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  send,
  withQuery,
} from '@/lib/api'
import { filenameFromContentDisposition } from '@/lib/download-blob'
import type { components } from '@/lib/openapi.generated'
import type { FilterCriteria } from '@/lib/dashboards'

type Schemas = components['schemas']

export type Agent = Schemas['AgentDto']

export type AgentDetail = Schemas['AgentDetailDto']
export type AgentConnectionHistory = Schemas['AgentConnectionHistoryDto']

export type AgentReleaseChannel = Schemas['AgentReleaseChannelDto']

export type AgentHostInfo = Schemas['AgentHostInfoDto']

export type AgentOperation = Schemas['AgentOperationDto']

export type AgentOperationDetail = Schemas['AgentOperationDetailDto']

export type AgentOperationTask = Schemas['AgentOperationTaskDto']
export type AgentDiagnosticBundle = Schemas['AgentDiagnosticBundleDto']
export type CreateAgentDiagnosticBundleResponse = Schemas['CreateAgentDiagnosticBundleResponse']
export type AgentDiagnosticBundleDownload = Schemas['AgentDiagnosticBundleDownloadResponse']

export type AgentOperationTaskLog = {
  text: string
  taskStatus: string
  terminal: boolean
  availability: string
  logSizeBytes: number
  truncated: boolean
}

export type AgentUpgradeTarget = Schemas['AgentUpgradeTargetDto']

export type DiscoveryProfile = Schemas['EntityAgentDiscoveryProfileDto']
export type DiscoveryProfileUpdate = NonNullable<
  Schemas['EntityAgentDiscoveryProfileUpdateDto']
>
export type EntityAgentConfiguration = Omit<
  Schemas['EntityAgentConfigurationDto'],
  'discoveryProfile'
> & { discoveryProfile: DiscoveryProfile }
export type EntityAgentDatasetPolicy = Schemas['EntityAgentDatasetPolicyDto']
export type EntityAgentIdentityPolicy = Schemas['EntityAgentIdentityPolicyDto']
export type EntityAgentDatasetPolicyUpdate =
  Schemas['EntityAgentDatasetPolicyUpdateDto']
type ReplaceEntityAgentConfigurationRequest =
  Schemas['ReplaceEntityAgentConfigurationRequestDto']
export type ReplaceEntityAgentConfigurationOptions = {
  expectedRevision: number
  datasetPolicies?: NonNullable<
    ReplaceEntityAgentConfigurationRequest['datasetPolicies']
  >
  ipExclusions?: NonNullable<
    ReplaceEntityAgentConfigurationRequest['ipExclusions']
  >
  discoveryProfile?: DiscoveryProfileUpdate
  adHocMaxTargetPrefixLength?: number
}
export type ReplaceEntityAgentConfigurationResponse =
  Schemas['ReplaceEntityAgentConfigurationResponseDto']

export type AgentDatasetSummary = Schemas['AgentDatasetSummaryDto']
export type AgentDatasetSnapshot = Schemas['AgentDatasetSnapshotDto']
export type AgentDatasetCollectionOptions = Schemas['AgentDatasetCollectionOptionsDto']
export type CollectAgentDatasetResponse = Schemas['CollectAgentDatasetResponse']
export type DiscoverAgentTargetsResponse = Schemas['DiscoverAgentTargetsResponse']
export type AgentTargetDiscovery = Schemas['AgentTargetDiscoveryResponse']
export type AgentDatasetRevision = Schemas['AgentDatasetRevisionDto']
export type AgentDatasetRevisionDetail = Schemas['AgentDatasetRevisionDetailDto']
export type AgentDatasetReconciliation = Schemas['AgentDatasetReconciliationDto']
export type DatasetDiagnosticValue = Schemas['DatasetDiagnosticValueDto']
export type CanonicalAssetDatasetSummary = Schemas['CanonicalAssetDatasetSummaryDto']
export type CanonicalAssetDatasetSnapshot = Schemas['CanonicalAssetDatasetSnapshotDto']

export type AgentDatasetSelector = {
  localSubjectId: string
  datasetType: string
  instanceKey: string
}

function decodeDatasetSummary(raw: unknown): AgentDatasetSummary {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset summary')
  return {
    localSubjectId: requireString(raw.localSubjectId, 'localSubjectId'),
    datasetType: requireString(raw.datasetType, 'datasetType'),
    instanceKey: requireString(raw.instanceKey, 'instanceKey'),
    epochId: requireString(raw.epochId, 'epochId'),
    revision: requireString(raw.revision, 'revision'),
    schemaVersion: requireNumber(raw.schemaVersion, 'schemaVersion'),
    collectorVersion: requireString(raw.collectorVersion, 'collectorVersion'),
    privacyClass: requireNumber(raw.privacyClass, 'privacyClass'),
    collectedAt: requireString(raw.collectedAt, 'collectedAt'),
    receivedAt: requireString(raw.receivedAt, 'receivedAt'),
  }
}

function decodeDatasetSnapshot(raw: unknown): AgentDatasetSnapshot {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset snapshot')
  return {
    dataset: decodeDatasetSummary(raw.dataset),
    readState: decodeAgentDatasetReadState(raw.readState),
    document: decodeDatasetDiagnosticValue(raw.document),
  }
}

function decodeAgentDatasetReadState(raw: unknown): Schemas['AgentDatasetReadStateDto'] {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset read state')
  return {
    status: requireString(raw.status, 'status'),
    freshness: requireString(raw.freshness, 'freshness'),
    statusRevision: nullableString(raw.statusRevision, 'statusRevision'),
    observedAt: nullableString(raw.observedAt, 'observedAt'),
    staleAt: nullableString(raw.staleAt, 'staleAt'),
    requestedIntervalSeconds: nullableString(raw.requestedIntervalSeconds, 'requestedIntervalSeconds'),
    diagnostic: requireString(raw.diagnostic, 'diagnostic'),
  }
}

function decodeDatasetCollectionOptions(raw: unknown): AgentDatasetCollectionOptions {
  if (!isRecord(raw) || !Array.isArray(raw.datasetTypes) || !isRecord(raw.statusRevisions)) {
    throw new Error('Malformed agent dataset collection options')
  }
  return {
    datasetTypes: raw.datasetTypes.map((datasetType) =>
      requireString(datasetType, 'datasetType')),
    statusRevisions: Object.fromEntries(
      Object.entries(raw.statusRevisions).map(([datasetType, revision]) =>
        [datasetType, requireNumber(revision, 'statusRevision')]),
    ),
  }
}

function decodeCollectAgentDatasetResponse(raw: unknown): CollectAgentDatasetResponse {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset collection response')
  return {
    operationId: requireString(raw.operationId, 'operationId'),
  }
}

function decodeStrings(raw: unknown, name: string): string[] {
  return requireArray(raw, name, (item) => requireString(item, name))
}

function decodeDiscoverAgentTargetsResponse(raw: unknown): DiscoverAgentTargetsResponse {
  if (!isRecord(raw)) throw new Error('Malformed discovery request response')
  return {
    operationId: nullableString(raw.operationId, 'operationId'),
    normalizedTargets: decodeStrings(raw.normalizedTargets, 'normalizedTargets'),
    targetCount: requireNumber(raw.targetCount, 'targetCount'),
    excludedTargets: decodeStrings(raw.excludedTargets, 'excludedTargets'),
    excludedCount: requireNumber(raw.excludedCount, 'excludedCount'),
    maxSelectors: requireNumber(raw.maxSelectors, 'maxSelectors'),
    maxTargets: requireNumber(raw.maxTargets, 'maxTargets'),
    startBy: nullableString(raw.startBy, 'startBy'),
    expiresAt: nullableString(raw.expiresAt, 'expiresAt'),
    invalidTargets: requireArray(raw.invalidTargets, 'invalidTargets', (item) => {
      if (!isRecord(item)) throw new Error('Malformed invalid discovery target')
      return {
        index: requireNumber(item.index, 'index'),
        target: requireString(item.target, 'target'),
        reason: requireString(item.reason, 'reason'),
      }
    }),
    problem: nullableString(raw.problem, 'problem'),
  }
}

function decodeAgentTargetDiscovery(raw: unknown): AgentTargetDiscovery {
  if (!isRecord(raw)) throw new Error('Malformed discovery request')
  return {
    operationId: requireString(raw.operationId, 'operationId'),
    agentId: requireString(raw.agentId, 'agentId'),
    requestedBy: requireString(raw.requestedBy, 'requestedBy'),
    targets: decodeStrings(raw.targets, 'targets'),
    targetCount: requireNumber(raw.targetCount, 'targetCount'),
    excludedCount: requireNumber(raw.excludedCount, 'excludedCount'),
    phase: requireString(raw.phase, 'phase'),
    responsiveCount: requireNumber(raw.responsiveCount, 'responsiveCount'),
    unresponsiveCount: requireNumber(raw.unresponsiveCount, 'unresponsiveCount'),
    failedCount: requireNumber(raw.failedCount, 'failedCount'),
    ineligibleCount: requireNumber(raw.ineligibleCount, 'ineligibleCount'),
    unattemptedCount: requireNumber(raw.unattemptedCount, 'unattemptedCount'),
    profiledCount: requireNumber(raw.profiledCount, 'profiledCount'),
    outcome: nullableString(raw.outcome, 'outcome'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    startBy: requireString(raw.startBy, 'startBy'),
    expiresAt: requireString(raw.expiresAt, 'expiresAt'),
    cancelRequestedAt: nullableString(raw.cancelRequestedAt, 'cancelRequestedAt'),
    stoppedAt: nullableString(raw.stoppedAt, 'stoppedAt'),
    backgroundPriorityUntil: nullableString(raw.backgroundPriorityUntil, 'backgroundPriorityUntil'),
  }
}

function decodeAgentDiagnosticBundle(raw: unknown): AgentDiagnosticBundle {
  if (!isRecord(raw)) throw new Error('Malformed agent diagnostic bundle')
  return {
    diagnosticBundleId: requireString(raw.diagnosticBundleId, 'diagnosticBundleId'),
    fileName: requireString(raw.fileName, 'fileName'),
    status: requireString(raw.status, 'status'),
    errorMessage: nullableString(raw.errorMessage, 'errorMessage'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    finishedAt: nullableString(raw.finishedAt, 'finishedAt'),
    expiresAt: requireString(raw.expiresAt, 'expiresAt'),
    available: requireBoolean(raw.available, 'available'),
  }
}

function decodeAgentDiagnosticBundles(raw: unknown): AgentDiagnosticBundle[] {
  if (!Array.isArray(raw)) throw new Error('Malformed agent diagnostic bundle list')
  return raw.map(decodeAgentDiagnosticBundle)
}

function decodeCreateAgentDiagnosticBundleResponse(
  raw: unknown,
): CreateAgentDiagnosticBundleResponse {
  if (!isRecord(raw)) throw new Error('Malformed diagnostic bundle response')
  return {
    diagnosticBundleId: requireString(raw.diagnosticBundleId, 'diagnosticBundleId'),
  }
}

function decodeAgentDiagnosticBundleDownload(raw: unknown): AgentDiagnosticBundleDownload {
  if (!isRecord(raw)) throw new Error('Malformed diagnostic bundle download')
  return {
    downloadUrl: requireString(raw.downloadUrl, 'downloadUrl'),
    fileName: requireString(raw.fileName, 'fileName'),
    expiresAt: requireString(raw.expiresAt, 'expiresAt'),
  }
}

function decodeDatasetRevision(raw: unknown): AgentDatasetRevision {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset revision')
  return {
    epochId: requireString(raw.epochId, 'epochId'),
    baseRevision: requireString(raw.baseRevision, 'baseRevision'),
    revision: requireString(raw.revision, 'revision'),
    changeKind: requireString(raw.changeKind, 'changeKind'),
    contentChanged: requireBoolean(raw.contentChanged, 'contentChanged'),
    schemaVersion: requireNumber(raw.schemaVersion, 'schemaVersion'),
    collectorVersion: requireString(raw.collectorVersion, 'collectorVersion'),
    privacyClass: requireNumber(raw.privacyClass, 'privacyClass'),
    collectedAt: requireString(raw.collectedAt, 'collectedAt'),
    receivedAt: requireString(raw.receivedAt, 'receivedAt'),
    checkpointFound: requireBoolean(raw.checkpointFound, 'checkpointFound'),
  }
}

function decodeDatasetRevisionDetail(raw: unknown): AgentDatasetRevisionDetail {
  const revision = decodeDatasetRevision(raw)
  if (!isRecord(raw)) throw new Error('Malformed agent dataset revision detail')
  return {
    ...revision,
    changeJson: requireString(raw.changeJson, 'changeJson'),
    document: raw.document === null ? null : decodeDatasetDiagnosticValue(raw.document),
  }
}

function decodeDatasetDiagnosticValue(raw: unknown): DatasetDiagnosticValue {
  if (!isRecord(raw)) throw new Error('Malformed diagnostic value')
  const kind = requireString(raw.kind, 'kind')
  const value: DatasetDiagnosticValue = { kind }
  switch (kind) {
    case 'null': return value
    case 'bool': return { ...value, bool: requireBoolean(raw.bool, 'bool') }
    case 'string': return { ...value, string: requireString(raw.string, 'string') }
    case 'int': return { ...value, int: requireString(raw.int, 'int') }
    case 'uint': return { ...value, uint: requireString(raw.uint, 'uint') }
    case 'double': return { ...value, double: requireNumber(raw.double, 'double') }
    case 'bytes': return { ...value, bytes: requireString(raw.bytes, 'bytes') }
    case 'timestamp': return { ...value, timestamp: requireString(raw.timestamp, 'timestamp') }
    case 'list':
      if (!Array.isArray(raw.list)) throw new Error('Malformed diagnostic list')
      return { ...value, list: raw.list.map(decodeDatasetDiagnosticValue) }
    case 'object':
      if (!isRecord(raw.object)) throw new Error('Malformed diagnostic object')
      return { ...value, object: Object.fromEntries(Object.entries(raw.object).map(([key, child]) => [key, decodeDatasetDiagnosticValue(child)])) }
    case 'opaque':
      if (!isRecord(raw.opaque)) throw new Error('Malformed diagnostic opaque value')
      return { ...value, opaque: {
        media_type: requireString(raw.opaque.media_type, 'media_type'),
        schema_uri: nullableString(raw.opaque.schema_uri, 'schema_uri'),
        data: requireString(raw.opaque.data, 'data'),
      } }
    default: throw new Error(`Unsupported diagnostic value kind: ${kind}`)
  }
}

function decodeAgentConnectionHistory(raw: unknown): AgentConnectionHistory {
  if (!isRecord(raw)) throw new Error('Malformed agent connection history')
  return {
    asOf: requireTimestamp(raw.asOf, 'asOf'),
    observedFrom: raw.observedFrom === null ? null : requireTimestamp(raw.observedFrom, 'observedFrom'),
    periods: requireArray(raw.periods, 'connection periods', (period) => {
      if (!isRecord(period)) throw new Error('Malformed agent connection period')
      return {
        connectedAt: requireTimestamp(period.connectedAt, 'connectedAt'),
        disconnectedAt: requireTimestamp(period.disconnectedAt, 'disconnectedAt'),
      }
    }),
  } satisfies AgentConnectionHistory
}

function decodeCanonicalAssetDatasetSummary(raw: unknown): CanonicalAssetDatasetSummary {
  if (!isRecord(raw)) throw new Error('Malformed canonical asset dataset summary')
  return { agentId: requireString(raw.agentId, 'agentId'), localSubjectId: requireString(raw.localSubjectId, 'localSubjectId'), datasetType: requireString(raw.datasetType, 'datasetType'), instanceKey: requireString(raw.instanceKey, 'instanceKey'), epochId: requireString(raw.epochId, 'epochId'), revision: requireString(raw.revision, 'revision'), schemaVersion: requireNumber(raw.schemaVersion, 'schemaVersion'), collectedAt: requireString(raw.collectedAt, 'collectedAt'), receivedAt: requireString(raw.receivedAt, 'receivedAt') }
}

const decodeDatasetSummaryPage = (raw: unknown) =>
  decodePage(raw, decodeDatasetSummary, 'agent dataset page')

const decodeDatasetRevisionPage = (raw: unknown) =>
  decodePage(raw, decodeDatasetRevision, 'agent dataset revision page')

function decodeDatasetReconciliation(raw: unknown): AgentDatasetReconciliation {
  if (!isRecord(raw) || !Array.isArray(raw.decisions)) {
    throw new Error('Malformed agent dataset reconciliation')
  }
  const state = raw.state
  const asset = raw.asset
  const observedSubject = raw.observedSubject
  const lineage = raw.lineage
  if (!isRecord(observedSubject) || !isRecord(lineage)) {
    throw new Error('Malformed agent dataset reconciliation context')
  }
  return {
    applicable: requireBoolean(raw.applicable, 'applicable'),
    observedSubject: {
      sourceAgentId: requireString(observedSubject.sourceAgentId, 'sourceAgentId'),
      localSubjectId: requireString(observedSubject.localSubjectId, 'localSubjectId'),
      sourceScope: requireString(observedSubject.sourceScope, 'sourceScope'),
    },
    lineage: {
      status: requireString(lineage.status, 'status'),
      reason: nullableString(lineage.reason, 'reason'),
      datasetAuthorizationId: nullableString(
        lineage.datasetAuthorizationId,
        'datasetAuthorizationId',
      ),
      datasetAuthorizationGeneration: nullableString(
        lineage.datasetAuthorizationGeneration,
        'datasetAuthorizationGeneration',
      ),
      currentAuthorizationId: nullableString(
        lineage.currentAuthorizationId,
        'currentAuthorizationId',
      ),
      currentDecisionGeneration: nullableString(
        lineage.currentDecisionGeneration,
        'currentDecisionGeneration',
      ),
      validUntil: nullableString(lineage.validUntil, 'validUntil'),
      updatedAt: nullableString(lineage.updatedAt, 'updatedAt'),
    },
    status: requireString(raw.status, 'status'),
    state: state === null ? null : decodeReconciliationState(state),
    asset: asset === null ? null : decodeCanonicalAsset(asset),
    decisions: raw.decisions.map((decision) => {
      if (!isRecord(decision)) throw new Error('Malformed reconciliation decision')
      return {
        decisionId: requireString(decision.decisionId, 'decisionId'),
        epochId: requireString(decision.epochId, 'epochId'),
        revision: requireString(decision.revision, 'revision'),
        outcome: requireString(decision.outcome, 'outcome'),
        assetId: nullableString(decision.assetId, 'assetId'),
        evidenceJson: requireString(decision.evidenceJson, 'evidenceJson'),
        createdAt: requireString(decision.createdAt, 'createdAt'),
      }
    }),
    nextCursor: nullableString(raw.nextCursor, 'nextCursor'),
  }
}

function decodeReconciliationState(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed reconciliation state')
  return {
    status: requireString(raw.status, 'status'),
    assetId: nullableString(raw.assetId, 'assetId'),
    epochId: requireString(raw.epochId, 'epochId'),
    revision: requireString(raw.revision, 'revision'),
    evidenceJson: requireString(raw.evidenceJson, 'evidenceJson'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
  }
}

function decodeCanonicalAsset(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed canonical asset')
  return {
    assetId: requireString(raw.assetId, 'assetId'),
    lifecycleState: requireString(raw.lifecycleState, 'lifecycleState'),
    deviceClass: nullableString(raw.deviceClass, 'deviceClass'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
  }
}

function datasetPath(entityId: string, agentId: string, suffix = '') {
  return `/api/v1/entities/${entityId}/agents/${agentId}/datasets${suffix}`
}

function datasetQuery(selector: AgentDatasetSelector, after?: string) {
  return {
    localSubjectId: selector.localSubjectId,
    datasetType: selector.datasetType,
    instanceKey: selector.instanceKey,
    after,
  }
}

export type AgentPskCreated = Schemas['AgentPskCreatedResponse']

function decodeAgentPskCreated(raw: unknown): AgentPskCreated {
  if (!isRecord(raw)) throw new Error('Malformed created pre-shared key')
  return {
    id: requireString(raw.id, 'id'),
    psk: requireString(raw.psk, 'psk'),
  } satisfies AgentPskCreated
}

function decodeEntityAgentDatasetPolicy(raw: unknown): EntityAgentDatasetPolicy {
  if (!isRecord(raw)) throw new Error('Malformed agent dataset policy')
  return {
    datasetType: requireString(raw.datasetType, 'datasetType'),
    enabled: requireBoolean(raw.enabled, 'enabled'),
    required: requireBoolean(raw.required, 'required'),
    displayName: requireString(raw.displayName, 'displayName'),
    description: requireString(raw.description, 'description'),
    categoryKey: requireString(raw.categoryKey, 'categoryKey'),
    categoryName: requireString(raw.categoryName, 'categoryName'),
    categoryOrder: requireNumber(raw.categoryOrder, 'categoryOrder'),
    displayOrder: requireNumber(raw.displayOrder, 'displayOrder'),
  } satisfies EntityAgentDatasetPolicy
}

function decodeEntityAgentIdentityPolicy(raw: unknown): EntityAgentIdentityPolicy {
  if (!isRecord(raw)) throw new Error('Malformed agent identity policy')
  return {
    sourceScope: requireString(raw.sourceScope, 'sourceScope'),
    policyId: requireString(raw.policyId, 'policyId'),
    generation: requireNumber(raw.generation, 'generation'),
    required: requireBoolean(raw.required, 'required'),
    active: requireBoolean(raw.active, 'active'),
  } satisfies EntityAgentIdentityPolicy
}

function decodeEntityAgentConfiguration(raw: unknown): EntityAgentConfiguration {
  const identityPolicies = isRecord(raw) ? raw.identityPolicies : undefined
  if (
    !isRecord(raw) ||
    !Array.isArray(raw.datasetPolicies) ||
    !Array.isArray(raw.ipExclusions) ||
    (identityPolicies !== undefined && !Array.isArray(identityPolicies))
  ) {
    throw new Error('Malformed entity agent configuration')
  }
  return {
    revision: requireNumber(raw.revision, 'revision'),
    datasetPolicies: raw.datasetPolicies.map(decodeEntityAgentDatasetPolicy),
    ipExclusions: raw.ipExclusions.map((value, index) =>
      requireString(value, `ipExclusions[${index}]`),
    ),
    identityPolicies: (identityPolicies ?? []).map(decodeEntityAgentIdentityPolicy),
    discoveryProfile: requireDiscoveryProfile(raw.discoveryProfile),
    adHocMaxTargetPrefixLength: raw.adHocMaxTargetPrefixLength === undefined
      ? defaultAdHocMaxTargetPrefixLength
      : requireNumber(raw.adHocMaxTargetPrefixLength, 'adHocMaxTargetPrefixLength'),
  } satisfies EntityAgentConfiguration
}

const defaultAdHocMaxTargetPrefixLength = 16

function requireDiscoveryProfile(raw: unknown): DiscoveryProfile {
  if (raw === undefined) return 'quiet'
  if (
    raw === 'quiet'
    || raw === 'balanced'
    || raw === 'fast'
    || raw === 'custom'
  ) return raw
  throw new Error('Malformed discovery profile')
}

function decodeReplaceEntityAgentConfiguration(
  raw: unknown,
): ReplaceEntityAgentConfigurationResponse {
  if (!isRecord(raw)) throw new Error('Malformed configuration replacement')
  return {
    revision: requireNumber(raw.revision, 'revision'),
    scheduledAgents: requireNumber(raw.scheduledAgents, 'scheduledAgents'),
  } satisfies ReplaceEntityAgentConfigurationResponse
}

export type AgentInstallerPlatform =
  | 'windows'
  | 'macos'
  | 'linux_deb'
  | 'linux_rpm'

export type AgentInstallerDownload = {
  blob: Blob
  filename: string
}

function decodeLogChunk(data: string, decoder: TextDecoder): string {
  const binary = atob(data)
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
  return decoder.decode(bytes, { stream: true })
}

type OperationTaskLogPage = Omit<AgentOperationTaskLog, 'truncated'> & {
  nextOffset: number
}

async function readOperationTaskLogPage(
  response: Response,
  decoder: TextDecoder,
): Promise<OperationTaskLogPage> {
  const frames = (await response.text()).split('\n').filter(Boolean)
  let text = ''
  let taskStatus = 'unknown'
  let terminal = false
  let availability = 'missing'
  let logSizeBytes = 0
  let nextOffset = 0

  for (const line of frames) {
    const frame: unknown = JSON.parse(line)
    if (!isRecord(frame)) throw new Error('Malformed operation log frame')
    if (frame.type === 'meta') {
      taskStatus = requireString(frame.taskStatus, 'taskStatus')
      terminal = requireBoolean(frame.terminal, 'terminal')
      availability = requireString(frame.availability, 'availability')
      logSizeBytes = requireNumber(frame.logSizeBytes, 'logSizeBytes')
    } else if (frame.type === 'chunk') {
      text += decodeLogChunk(requireString(frame.data, 'data'), decoder)
    } else if (frame.type === 'end') {
      logSizeBytes = requireNumber(frame.logSizeBytes, 'logSizeBytes')
      nextOffset = requireNumber(frame.nextOffset, 'nextOffset')
    } else if (frame.type === 'error') {
      throw new Error(requireString(frame.code, 'code'))
    }
  }

  return { text, taskStatus, terminal, availability, logSizeBytes, nextOffset }
}

const operationLogPageBytes = 4 * 1024 * 1024
const operationLogViewerBytes = 16 * 1024 * 1024

export type GroupKeyValue = string | number | null
export type GroupCounts = { fields: string[]; groups: { key: GroupKeyValue[]; count: number }[]; otherCount: number }
export type BucketCount = { bucket: number | null; count: number }
type SummaryGroups = 'os' | 'outdatedByEntity' | 'offlineRecentByEntity' | 'offlineStaleByEntity' | 'releases'
export type AgentFleetSummary = Omit<Schemas['AgentFleetSummary'], SummaryGroups | 'offlineAge' | 'lastSeen' | 'uptimeByHour'>
  & Record<SummaryGroups, GroupCounts>
  & { offlineAge: BucketCount[]; lastSeen: BucketCount[]; uptimeByHour: UptimeHour[] }
export type UptimeHour = { dayOfWeek: number; hour: number; connectedMinutes: number; possibleMinutes: number }

function decodeGroupCounts(raw: unknown, name: string): GroupCounts {
  if (!isRecord(raw)) throw new Error(`Malformed ${name}`)
  return {
    fields: requireArray(raw.fields, `${name}.fields`, (field) => requireString(field, `${name}.fields`)),
    groups: requireArray(raw.groups, `${name}.groups`, (group) => {
      if (!isRecord(group)) throw new Error(`Malformed ${name}.groups`)
      return {
        key: requireArray(group.key, `${name}.key`, (value) => {
          if (value !== null && typeof value !== 'string' && typeof value !== 'number') throw new Error(`Malformed ${name}.key`)
          return value
        }),
        count: requireNumber(group.count, `${name}.count`),
      }
    }),
    otherCount: requireNumber(raw.otherCount, `${name}.otherCount`),
  }
}

function decodeBuckets(raw: unknown, name: string): BucketCount[] {
  return requireArray(raw, name, (bucket) => {
    if (!isRecord(bucket)) throw new Error(`Malformed ${name}`)
    return {
      bucket: bucket.bucket === null ? null : requireNumber(bucket.bucket, `${name}.bucket`),
      count: requireNumber(bucket.count, `${name}.count`),
    }
  })
}

function decodeAgentFleetSummary(raw: unknown): AgentFleetSummary {
  if (!isRecord(raw)) throw new Error('Malformed agent fleet summary')
  const number = (field: string) => requireNumber(raw[field], field)
  const groups = (field: string) => decodeGroupCounts(raw[field], field)
  return {
    total: number('total'),
    live: number('live'),
    online: number('online'),
    current: number('current'),
    needAttention: number('needAttention'),
    offlineOverWeek: number('offlineOverWeek'),
    behind: number('behind'),
    unsupportedOs: number('unsupportedOs'),
    offlineAge: decodeBuckets(raw.offlineAge, 'offlineAge'),
    os: groups('os'),
    outdatedByEntity: groups('outdatedByEntity'),
    offlineRecentByEntity: groups('offlineRecentByEntity'),
    offlineStaleByEntity: groups('offlineStaleByEntity'),
    releases: groups('releases'),
    lastSeen: decodeBuckets(raw.lastSeen, 'lastSeen'),
    lastSeenEdgesHours: requireArray(raw.lastSeenEdgesHours, 'lastSeenEdgesHours', (hours) => requireNumber(hours, 'lastSeenEdgesHours')),
    uptimeByHour: requireArray(raw.uptimeByHour, 'uptimeByHour', (cell) => {
      if (!isRecord(cell)) throw new Error('Malformed uptimeByHour')
      return {
        dayOfWeek: requireNumber(cell.dayOfWeek, 'uptimeByHour.dayOfWeek'),
        hour: requireNumber(cell.hour, 'uptimeByHour.hour'),
        connectedMinutes: requireNumber(cell.connectedMinutes, 'uptimeByHour.connectedMinutes'),
        possibleMinutes: requireNumber(cell.possibleMinutes, 'uptimeByHour.possibleMinutes'),
      }
    }),
    uptimeWindowDays: number('uptimeWindowDays'),
  }
}

export const agentsApi = {
  summarizeAgents: (entityId: string, criteria: FilterCriteria, signal?: AbortSignal) =>
    request(
      `/api/v1/entities/${entityId}/agents:summarize`,
      decodeAgentFleetSummary,
      { ...json('POST', criteria), signal },
    ),
  getAgentConnectionHistory: (entityId: string, agentId: string) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/connection-history`,
      decodeAgentConnectionHistory,
    ),
  getAgentDiagnosticBundles: (entityId: string, agentId: string) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/diagnostic-bundles`,
      decodeAgentDiagnosticBundles,
    ),
  discoverAgentTargets: (entityId: string, agentId: string, targets: string[], validateOnly: boolean) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/discovery-requests`,
      decodeDiscoverAgentTargetsResponse,
      json('POST', { targets, validateOnly }),
    ),
  getAgentTargetDiscovery: (entityId: string, agentId: string, operationId: string) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/discovery-requests/${operationId}`,
      decodeAgentTargetDiscovery,
    ),
  cancelAgentTargetDiscovery: (entityId: string, agentId: string, operationId: string) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/discovery-requests/${operationId}/cancel`,
      decodeAgentTargetDiscovery,
      { method: 'POST' },
    ),
  createAgentDiagnosticBundle: (entityId: string, agentId: string) =>
    request(
      `/api/v1/entities/${entityId}/agents/${agentId}/diagnostic-bundles`,
      decodeCreateAgentDiagnosticBundleResponse,
      { method: 'POST' },
    ),
  getAgentDiagnosticBundleDownload: (
    entityId: string,
    agentId: string,
    diagnosticBundleId: string,
  ) => request(
    `/api/v1/entities/${entityId}/agents/${agentId}/diagnostic-bundles/${diagnosticBundleId}/download-url`,
    decodeAgentDiagnosticBundleDownload,
    { method: 'POST' },
  ),
  getCanonicalAssetDatasets: (entityId: string, assetId: string) => request(
    `/api/v1/entities/${entityId}/assets/${assetId}/datasets`,
    (raw) => requireArray(raw, 'canonical asset datasets', decodeCanonicalAssetDatasetSummary),
  ),
  getCanonicalAssetDatasetSnapshot: (entityId: string, assetId: string, selector: AgentDatasetSelector & { agentId: string }) => conditionalRequest(
    withQuery(`/api/v1/entities/${entityId}/assets/${assetId}/datasets/snapshot`, selector),
    (raw): CanonicalAssetDatasetSnapshot => {
      if (!isRecord(raw)) throw new Error('Malformed canonical asset dataset snapshot')
      return {
        dataset: decodeCanonicalAssetDatasetSummary(raw.dataset),
        readState: decodeAgentDatasetReadState(raw.readState),
        document: decodeDatasetDiagnosticValue(raw.document),
      }
    },
  ),
  collectAgentDataset: (entityId: string, agentId: string, datasetType: string) =>
    request(
      datasetPath(entityId, agentId, '/collect'),
      decodeCollectAgentDatasetResponse,
      json('POST', { datasetType } satisfies Schemas['CollectAgentDatasetRequest']),
    ),
  getAgentDatasets: (entityId: string, agentId: string, after?: string) =>
    request(
      withQuery(datasetPath(entityId, agentId), { after }),
      decodeDatasetSummaryPage,
    ),
  getAgentDatasetCollectionOptions: (entityId: string, agentId: string) =>
    request(
      datasetPath(entityId, agentId, '/collection-options'),
      decodeDatasetCollectionOptions,
    ),
  getAgentDatasetSnapshot: (
    entityId: string,
    agentId: string,
    selector: AgentDatasetSelector,
  ) => conditionalRequest(
    withQuery(datasetPath(entityId, agentId, '/snapshot'), datasetQuery(selector)),
    decodeDatasetSnapshot,
  ),
  getAgentDatasetRevisions: (
    entityId: string,
    agentId: string,
    selector: AgentDatasetSelector,
    after?: string,
  ) => request(
    withQuery(datasetPath(entityId, agentId, '/revisions'), datasetQuery(selector, after)),
    decodeDatasetRevisionPage,
  ),
  getAgentDatasetRevision: (
    entityId: string,
    agentId: string,
    selector: AgentDatasetSelector,
    epochId: string,
    revision: string,
  ) => request(
    withQuery(
      datasetPath(entityId, agentId, `/revisions/${epochId}/${revision}`),
      datasetQuery(selector),
    ),
    decodeDatasetRevisionDetail,
  ),
  getAgentDatasetReconciliation: (
    entityId: string,
    agentId: string,
    selector: AgentDatasetSelector,
    after?: string,
  ) => request(
    withQuery(datasetPath(entityId, agentId, '/reconciliation'), datasetQuery(selector, after)),
    decodeDatasetReconciliation,
  ),
  getEntityAgentDetail: async (
    entityId: string,
    agentId: string,
  ): Promise<AgentDetail> => {
    const response = await send(`/api/v1/entities/${entityId}/agents/${agentId}`)
    return response.json()
  },
  updateAgentReleaseChannel: (entityId: string, agentId: string, channelId: string) =>
    requestNoContent(
      `/api/v1/entities/${entityId}/agents/${agentId}/release-channel`,
      json('PUT', { channelId }),
    ),
  getAgentOperationDetail: async (
    entityId: string,
    agentId: string,
    operationId: string,
  ): Promise<AgentOperationDetail> => {
    const response = await send(
      `/api/v1/entities/${entityId}/agents/${agentId}/operations/${operationId}`,
    )
    return response.json()
  },
  getAgentOperationTaskLog: async (
    entityId: string,
    agentId: string,
    operationId: string,
    taskId: string,
  ): Promise<AgentOperationTaskLog> => {
    const decoder = new TextDecoder()
    let offset = 0
    let text = ''
    let latest: OperationTaskLogPage | null = null

    while (offset < operationLogViewerBytes) {
      const limit = Math.min(operationLogPageBytes, operationLogViewerBytes - offset)
      const response = await send(
        `/api/v1/entities/${entityId}/agents/${agentId}/operations/${operationId}/tasks/${taskId}/logs?offset=${offset}&limit=${limit}`,
        { headers: { Accept: 'application/x-ndjson' } },
      )
      const page = await readOperationTaskLogPage(response, decoder)
      text += page.text
      latest = page
      if (page.nextOffset <= offset || page.nextOffset >= page.logSizeBytes) break
      offset = page.nextOffset
    }

    if (!latest) throw new Error('Operation log returned no frames')
    text += decoder.decode()
    return {
      text,
      taskStatus: latest.taskStatus,
      terminal: latest.terminal,
      availability: latest.availability,
      logSizeBytes: latest.logSizeBytes,
      truncated: latest.nextOffset < latest.logSizeBytes,
    }
  },
  createEntityAgentPsk: (
    entityId: string,
    options: { maxUses?: number; expiresAt?: string },
  ) =>
    request(
      `/api/v1/entities/${entityId}/agents/psks`,
      decodeAgentPskCreated,
      json('POST', options),
    ),
  downloadEntityAgentInstaller: async (
    entityId: string,
    platform: AgentInstallerPlatform,
    maxUses = 1,
  ): Promise<AgentInstallerDownload> => {
    const resp = await send(
      withQuery(`/api/v1/entities/${entityId}/agents/installer`, { platform, maxUses: String(maxUses) }),
    )
    const blob = await resp.blob()
    const filename = filenameFromContentDisposition(
      resp.headers.get('content-disposition'),
      `vantage-agent-${platform}`,
    )
    return { blob, filename }
  },
  revokeEntityAgent: (entityId: string, agentId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/agents/${agentId}/revoke`, {
      method: 'POST',
    }),
  getAgentUpgradeTargets: async (
    entityId: string,
    agentId: string,
  ): Promise<AgentUpgradeTarget[]> => {
    const response = await send(
      `/api/v1/entities/${entityId}/agents/${agentId}/upgrade-targets`,
    )
    return response.json()
  },
  upgradeAgent: (entityId: string, agentId: string, releaseId: string) =>
    requestNoContent(
      `/api/v1/entities/${entityId}/agents/${agentId}/upgrade`,
      json('POST', { releaseId }),
    ),
  disconnectAgent: (entityId: string, agentId: string, minutes: number) =>
    requestNoContent(
      `/api/v1/entities/${entityId}/agents/${agentId}/disconnect`,
      json('POST', { minutes }),
    ),
  revokeEntityAgentPsk: (entityId: string, pskId: string) =>
    requestNoContent(
      `/api/v1/entities/${entityId}/agents/psks/${pskId}/revoke`,
      { method: 'POST' },
    ),
  getEntityAgentConfiguration: (entityId: string) =>
    request(
      `/api/v1/entities/${entityId}/agent-configuration`,
      decodeEntityAgentConfiguration,
    ),
  replaceEntityAgentConfiguration: (
    entityId: string,
    options: ReplaceEntityAgentConfigurationOptions,
  ) =>
    request(
      `/api/v1/entities/${entityId}/agent-configuration`,
      decodeReplaceEntityAgentConfiguration,
      json('PUT', options),
    ),
}
